import React, { useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { EASE } from '../../../lib/motion';
import styles from '../Home.module.css';

/* ─── FAQ Accordion Item (ledger row) ─── */
const FAQItem: React.FC<{
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
  guideAnchor?: string;
}> = ({ question, answer, isOpen, onToggle, index, guideAnchor }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  const answerId = `faq-answer-${index}`;
  const questionId = `faq-question-${index}`;

  return (
    <motion.div
      ref={ref}
      className={styles.faqItem}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.06, ease: EASE }}
    >
      <h3 className={styles.faqHeading}>
        <button
          id={questionId}
          className={styles.faqQuestion}
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={isOpen ? answerId : undefined}
        >
          <span className={styles.faqQuestionText}>{question}</span>
          <span
            className={`${styles.faqIcon} ${isOpen ? styles.faqIconOpen : ''}`}
            aria-hidden="true"
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 4v12M4 10h12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </span>
        </button>
      </h3>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={answerId}
            role="region"
            aria-labelledby={questionId}
            className={styles.faqAnswer}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          >
            <p className={styles.faqAnswerText}>{answer}</p>
            {guideAnchor && (
              <Link to={`/guide#${guideAnchor}`} className={styles.faqGuideLink}>
                Read more in the guide <span aria-hidden="true">→</span>
              </Link>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FAQItem;
